import React from "react";
import { Col, Row, Typography, Divider, Alert, Card } from "antd";
import { FileTextOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from "@ant-design/icons";

const { Title, Paragraph, Text } = Typography;

const RequiredDocumentation = () => {
  return (
    <Row type="flex">
      <Col span={20} offset={1}>
        <Title level={2}>Required Documentation</Title>
        <Paragraph style={{ fontSize: '16px', marginBottom: '24px' }}>
          The following documentation is required to complete your i4 Vendor Profile and submit 
          a Pre-Qualification Questionnaire (PQQ). Please gather these documents before beginning 
          the profile completion process to ensure a smooth experience.
        </Paragraph>

        <Alert
          message="Document Preparation Tips"
          description={
            <div>
              <Paragraph style={{ marginBottom: '8px' }}>
                <CheckCircleOutlined style={{ marginRight: '8px', color: '#52c41a' }} />
                Most documents should be in PDF format
              </Paragraph>
              <Paragraph style={{ marginBottom: '8px' }}>
                <CheckCircleOutlined style={{ marginRight: '8px', color: '#52c41a' }} />
                Ensure documents are clear, legible, and not password-protected
              </Paragraph>
              <Paragraph style={{ marginBottom: 0 }}>
                <CheckCircleOutlined style={{ marginRight: '8px', color: '#52c41a' }} />
                Have digital copies ready before starting the upload process
              </Paragraph>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: '24px' }}
        />

        <Divider />

        <Title level={4}>Financial Documentation</Title>
        <Card style={{ marginBottom: '16px' }}>
          <Paragraph style={{ marginBottom: '8px' }}>
            <FileTextOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            <Text strong>Audited Financial Statement</Text>
          </Paragraph>
          <Paragraph style={{ marginLeft: '24px', marginBottom: 0, color: '#666' }}>
            Applicant Firm's (or Parent Firm's) most recent audited financial statement. 
            This should be the complete, professionally audited financial report.
          </Paragraph>
        </Card>

        <Title level={4}>Organizational Documentation</Title>
        <Card style={{ marginBottom: '16px' }}>
          <Paragraph style={{ marginBottom: '8px' }}>
            <FileTextOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            <Text strong>Entity Organization Chart</Text>
          </Paragraph>
          <Paragraph style={{ marginLeft: '24px', marginBottom: 0, color: '#666' }}>
            A visual representation of your company's organizational structure, showing 
            reporting relationships and key personnel.
          </Paragraph>
        </Card>

        <Title level={4}>Project & Contract Information</Title>
        <Card style={{ marginBottom: '16px' }}>
          <Paragraph style={{ marginBottom: '8px' }}>
            <FileTextOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            <Text strong>Contracts in Process</Text>
          </Paragraph>
          <Paragraph style={{ marginLeft: '24px', marginBottom: '8px', color: '#666' }}>
            Documentation of contracts in process and projects under contract but not yet started, 
            including:
          </Paragraph>
          <ul style={{ marginLeft: '48px', marginBottom: 0 }}>
            <li>Name of owner</li>
            <li>Architect</li>
            <li>Construction Manager and General Contractor (as relevant)</li>
            <li>Value of contract</li>
            <li>Percent complete and expected completion date</li>
          </ul>
        </Card>

        <Title level={4}>Compliance & Ethics</Title>
        <Card style={{ marginBottom: '16px' }}>
          <Paragraph style={{ marginBottom: '8px' }}>
            <FileTextOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            <Text strong>Code of Conduct/Compliance Program Manual</Text>
          </Paragraph>
          <Paragraph style={{ marginLeft: '24px', marginBottom: 0, color: '#666' }}>
            Your company's code of conduct and compliance program documentation, demonstrating 
            your commitment to ethical business practices.
          </Paragraph>
        </Card>

        <Title level={4}>Insurance Documentation</Title>
        <Card style={{ marginBottom: '16px' }}>
          <Paragraph style={{ marginBottom: '8px' }}>
            <FileTextOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            <Text strong>Sample Insurance Certificate</Text>
          </Paragraph>
          <Paragraph style={{ marginLeft: '24px', marginBottom: '8px', color: '#666' }}>
            A sample insurance certificate indicating current coverage carriers and limits for the following:
          </Paragraph>
          <ul style={{ marginLeft: '48px', marginBottom: 0 }}>
            <li>General liability</li>
            <li>Workers' compensation</li>
            <li>Auto liability</li>
            <li>Excess liability and professional liability</li>
          </ul>
        </Card>

        <Title level={4}>Safety Documentation</Title>
        <Card style={{ marginBottom: '16px' }}>
          <Paragraph style={{ marginBottom: '8px' }}>
            <FileTextOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            <Text strong>EMR Broker Letter</Text>
          </Paragraph>
          <Paragraph style={{ marginLeft: '24px', marginBottom: 0, color: '#666' }}>
            Broker letter indicating the applicant firm's Experience Modification Rate (EMR) for 
            the most recent three years.
          </Paragraph>
        </Card>

        <Card style={{ marginBottom: '16px' }}>
          <Paragraph style={{ marginBottom: '8px' }}>
            <FileTextOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            <Text strong>OSHA 300a Forms</Text>
          </Paragraph>
          <Paragraph style={{ marginLeft: '24px', marginBottom: 0, color: '#666' }}>
            Applicant firm's OSHA 300a forms (Summary of Work-Related Injuries and Illnesses) 
            for the most recent three years.
          </Paragraph>
        </Card>

        <Card style={{ marginBottom: '16px' }}>
          <Paragraph style={{ marginBottom: '8px' }}>
            <FileTextOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            <Text strong>Safety Director Resume</Text>
          </Paragraph>
          <Paragraph style={{ marginLeft: '24px', marginBottom: 0, color: '#666' }}>
            Resume for the Safety Director or individual responsible for safety management.
          </Paragraph>
        </Card>

        <Card style={{ marginBottom: '16px' }}>
          <Paragraph style={{ marginBottom: '8px' }}>
            <FileTextOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            <Text strong>Safety Policy and Program</Text>
          </Paragraph>
          <Paragraph style={{ marginLeft: '24px', marginBottom: 0, color: '#666' }}>
            Comprehensive documentation of your company's safety policies and safety program.
          </Paragraph>
        </Card>

        <Card style={{ marginBottom: '16px' }}>
          <Paragraph style={{ marginBottom: '8px' }}>
            <FileTextOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            <Text strong>Return to Work Policy and Program</Text>
          </Paragraph>
          <Paragraph style={{ marginLeft: '24px', marginBottom: 0, color: '#666' }}>
            Documentation of your return-to-work policy and program for injured employees.
          </Paragraph>
        </Card>

        <Card style={{ marginBottom: '16px' }}>
          <Paragraph style={{ marginBottom: '8px' }}>
            <FileTextOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            <Text strong>Drug Testing Policy</Text>
          </Paragraph>
          <Paragraph style={{ marginLeft: '24px', marginBottom: 0, color: '#666' }}>
            Your company's drug testing policy documentation.
          </Paragraph>
        </Card>

        <Divider />

        <Title level={4}>Document Upload Guidelines</Title>
        <Alert
          message="File Requirements"
          description={
            <ul style={{ marginBottom: 0, paddingLeft: '20px' }}>
              <li>Preferred format: PDF</li>
              <li>Ensure files are not password-protected</li>
              <li>Documents should be clear and legible</li>
              <li>File size limits may apply (check system requirements)</li>
              <li>Use descriptive file names when possible</li>
            </ul>
          }
          type="warning"
          showIcon
          style={{ marginBottom: '16px' }}
        />

        <Title level={4}>Timeline Considerations</Title>
        <Paragraph>
          <ExclamationCircleOutlined style={{ marginRight: '8px', color: '#fa8c16' }} />
          <Text strong>Important:</Text> Some documents may take time to obtain (e.g., audited financial 
          statements, broker letters). Plan ahead and gather all required documentation before beginning 
          your profile to avoid delays in the submission process.
        </Paragraph>

        <Divider />

        <Title level={4}>Need Help?</Title>
        <Paragraph>
          If you have questions about specific document requirements or need clarification on what 
          to include, please contact support at <Text strong>info@i4strategies.com</Text> or 
          <Text strong> 212-909-9580</Text>. Our team can help clarify requirements and ensure you 
          have everything needed for a successful submission.
        </Paragraph>

        <div style={{ marginTop: '32px', padding: '16px', backgroundColor: '#f0f2f5', borderRadius: '4px' }}>
          <Paragraph style={{ marginBottom: 0 }}>
            <Text strong>Tip:</Text> Create a checklist of all required documents and check them off 
            as you gather them. This will help ensure you don't miss anything during the profile 
            completion process.
          </Paragraph>
        </div>
      </Col>
    </Row>
  );
};

export default RequiredDocumentation;
